import { useGrantedPermissions } from '../hooks/useHasPermission';
import { useActiveLocation } from '../hooks/useActiveLocation';
import { SideMenuPageLayout } from '../components/SideMenuPageLayout/SideMenuPageLayout';
import type {
  PageSection,
  PageSectionGroup
} from '../components/SideMenuPageLayout/SideMenuPageLayout';
import ROUTES, {
  GLOBAL_SETTINGS_PERMISSIONS,
  LOCATION_SETTINGS_PERMISSIONS,
  locationPath,
  pagePermissions,
  permittedPages
} from '../routes/routeConstants';

const LOCATION_SETTINGS_PERMISSION_LIST = pagePermissions(LOCATION_SETTINGS_PERMISSIONS);
const GLOBAL_SETTINGS_PERMISSION_LIST = pagePermissions(GLOBAL_SETTINGS_PERMISSIONS);

const toLabelKey = (segment: string): string => `settings-menu-${segment}`;

export const SettingsPage = () => {
  // One entry per subpage the user actually holds the permission for, matching
  // the route guards. A subpage they cannot open is left out rather than shown
  // and then bounced by its guard.
  const grantedLocationPermissions = useGrantedPermissions(LOCATION_SETTINGS_PERMISSION_LIST);
  const grantedGlobalPermissions = useGrantedPermissions(GLOBAL_SETTINGS_PERMISSION_LIST);
  const { activeLocationId } = useActiveLocation();

  const groups: PageSectionGroup[] = [];

  // Location settings carry the active location in their URL. Hidden until a
  // location resolves (auto-selected to the first accessible one).
  if (activeLocationId) {
    const locationSections: PageSection[] = permittedPages(
      LOCATION_SETTINGS_PERMISSIONS,
      grantedLocationPermissions
    ).map(({ segment }) => ({
      to: locationPath(activeLocationId, ROUTES.settings, segment),
      labelKey: toLabelKey(segment)
    }));
    if (locationSections.length > 0) {
      groups.push({ labelKey: 'settings-group-location', sections: locationSections });
    }
  }

  // Global (account) settings live at flat /settings/*.
  const globalSections: PageSection[] = permittedPages(
    GLOBAL_SETTINGS_PERMISSIONS,
    grantedGlobalPermissions
  ).map(({ segment }) => ({
    to: `/${ROUTES.settings}/${segment}`,
    labelKey: toLabelKey(segment)
  }));

  if (globalSections.length > 0) {
    groups.push({ labelKey: 'settings-group-global', sections: globalSections });
  }

  return <SideMenuPageLayout headingKey="settings-header" groups={groups} />;
};
